/**
 * After GitLab OAuth redirect (esp. Private/Incognito), the session cookie is
 * sometimes not visible until a full page reload. We do that reload automatically
 * once for all UI sign-in paths — users should not need to refresh by hand.
 */

const FLAG_KEY = 'iron-phoenix.post-oauth-reload';

type Flag = 'pending' | 'reloaded';

function readFlag(): Flag | null {
  try {
    const v = sessionStorage.getItem(FLAG_KEY);
    if (v === 'pending' || v === 'reloaded') return v;
    return null;
  } catch {
    return null;
  }
}

function writeFlag(value: Flag | null): void {
  try {
    if (value === null) {
      sessionStorage.removeItem(FLAG_KEY);
    } else {
      sessionStorage.setItem(FLAG_KEY, value);
    }
  } catch {
    /* private mode / blocked storage */
  }
}

export function isCliLoginPath(pathname: string = window.location.pathname): boolean {
  return pathname.startsWith('/cli-login');
}

/** Call right before starting GitLab OAuth (UI sign-in or CLI login). */
export function markOAuthRedirectPending(): void {
  writeFlag('pending');
}

/**
 * If we just returned from OAuth and still have no session, reload once.
 * Returns true when a reload was triggered (caller should stop).
 */
export function maybeReloadOnceAfterOAuth(hasSession: boolean): boolean {
  const flag = readFlag();
  if (!flag) return false;

  if (hasSession) {
    writeFlag(null);
    return false;
  }

  if (flag === 'pending') {
    writeFlag('reloaded');
    window.location.reload();
    return true;
  }

  // Already reloaded once — clear and let the UI show sign-in / error.
  writeFlag(null);
  return false;
}

/** Clear the flag after sign-in successfully finishes. */
export function clearOAuthReloadFlag(): void {
  writeFlag(null);
}

/** @deprecated Use {@link markOAuthRedirectPending} */
export const markCliLoginOAuthPending = markOAuthRedirectPending;
/** @deprecated Use {@link maybeReloadOnceAfterOAuth} */
export const maybeReloadOnceAfterCliLoginOAuth = maybeReloadOnceAfterOAuth;
/** @deprecated Use {@link clearOAuthReloadFlag} */
export const clearCliLoginOAuthReloadFlag = clearOAuthReloadFlag;
